/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { Fragment, FunctionComponent, useMemo, useRef } from 'react';

import { ExpiryStatus } from 'client-code/vod-browse';

import { TIVO_SERVICES } from '../../../../config';
import { VodInteractor } from '../../../../core/interactors';
import { Locale, LocaleKeys } from '../../../../core/locale';
import { ImageUrlGenerator } from '../../../../core/utils';
import { ImageType } from '../../../../core/utils/ImageUrlGenerator';
import { ProgressBar } from '../../ProgressBar';
import { ContentTileImageType, ContentTileType, IContentTileProps } from './ContentTile.props';

import './ContentTile.scss';

const ContentTile: FunctionComponent<IContentTileProps> = ({
    type,
    title,
    digObjectId,
    videoResolution,
    percentWatched,
    expiryStatus,
    isAdult,
    seasonNumber,
    episodeNumber,
}): JSX.Element => {
    const titleRef = useRef<HTMLSpanElement>(null);
    const oImageUrl = new ImageUrlGenerator();
    // TODO: Needs to access it via defined method once interactors and
    // ServiceClientImpl are aligned.
    const vodInteractor = useMemo(() => new VodInteractor(), []);
    const shouldHideMetadata = vodInteractor.shouldHideMetadata();
    const isContentRestricted = shouldHideMetadata && isAdult;
    const baseImageUrl = VodInteractor.servicesBaseUrls[TIVO_SERVICES.ImageHost];
    const tileType = ContentTileType[type] || '';
    const containerTypeClass = tileType
        ? `content-tile-container ${tileType}`
        : 'content-tile-container';

    const imageType: ImageType =
        ContentTileImageType[tileType?.toUpperCase()] ?? ContentTileImageType.COLLECTION;
    const imageWidth = imageType === ContentTileImageType.MOVIE ? 252 : 504;
    const imageUrl = oImageUrl.buildImageUrl(
        baseImageUrl,
        digObjectId || '',
        imageType,
        imageWidth,
        378,
    );

    const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
        const target = event.target as HTMLImageElement;
        target.parentElement?.classList.add('with-background-image');
        if (titleRef.current) {
            titleRef.current.style.display = 'none';
        }
    };

    const formatEpisodeInfo = (season?: number, episode?: number): string => {
        if (!season && !episode) return '';
        const seasonStr = season ? `S${season}` : '';
        const episodeStr = episode ? `E${episode}` : '';
        return `${seasonStr} ${episodeStr}`.trim();
    };

    const episodeInfo = formatEpisodeInfo(seasonNumber, episodeNumber);

    const renderRestrictedTile = () => {
        return (
            <div className="restricted-content">
                {Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN)}
            </div>
        );
    };

    const renderUnrestrictedTile = () => {
        return (
            <Fragment>
                {imageUrl && <img src={imageUrl} alt="" onLoad={handleImageLoad} />}
                <div className="bottom-left-badges">
                    {expiryStatus && (
                        <div
                            className={
                                expiryStatus === ExpiryStatus.ExpiresIn24Hours
                                    ? 'expiry-status expires-in-24-hours'
                                    : 'expiry-status expires-in-72-hours'
                            }
                        ></div>
                    )}
                    {videoResolution && <span className="video-resolution">{videoResolution}</span>}
                </div>
                {title && <span ref={titleRef}>{title}</span>}
                {episodeInfo && <span className="episode-info">{episodeInfo}</span>}
                <div className="percent-watched">
                    <ProgressBar percentage={percentWatched} />
                </div>
            </Fragment>
        );
    };

    return (
        <div className={`${containerTypeClass}`}>
            {isContentRestricted ? renderRestrictedTile() : renderUnrestrictedTile()}
        </div>
    );
};

export default ContentTile;
